import { D as CANVAS_ID, P as WindowEventMap, Q as eventPath } from './bxf-utils-3908c3c5.js';
import { isEmpty } from 'lodash-es';
import { K as KeyboardEventKey } from './keyboard-event-key.enum-2f6c4d91.js';

/**
 * Method that counts textWidth using CanvasAPI
 */
const getTextWidth = (() => {
    let canvas = document.getElementById(CANVAS_ID);
    if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.style.display = 'none';
        canvas.style.position = 'absolute';
        canvas.style.top = '-9999em';
        canvas.setAttribute('id', CANVAS_ID);
        document.body.appendChild(canvas);
    }
    return (text, fontAttributes) => {
        const context = canvas.getContext('2d');
        if (!context)
            return 0;
        context.font = fontAttributes;
        return context.measureText(text).width;
    };
})();

/**
 * Sorts options such that exact matches are moved to the top
 * @param displayVal1
 * @param displayVal2
 */
function sortPredicate(inputValue, displayVal1, displayVal2) {
    if (isEmpty(inputValue))
        return 0;
    if (displayVal1 === inputValue && displayVal2 !== inputValue) {
        return -1;
    }
    else if (displayVal1 !== inputValue && displayVal2 === inputValue) {
        return 1;
    }
    return 0;
}

class BxfSelectKeyDownDelegate {
    delegate;
    selectedIndex = -1;
    constructor(delegate) {
        this.delegate = delegate;
        this.keyDownHandler = this.keyDownHandler.bind(this);
        this.mouseMoveHandler = this.mouseMoveHandler.bind(this);
        this.mouseOutHandler = this.mouseOutHandler.bind(this);
    }
    didChangeVisibility(hidden) {
        if (hidden) {
            window.removeEventListener(WindowEventMap.keydown, this.keyDownHandler);
            window.removeEventListener(WindowEventMap.mousemove, this.mouseMoveHandler);
            window.removeEventListener(WindowEventMap.mouseout, this.mouseOutHandler);
        }
        else {
            window.addEventListener(WindowEventMap.keydown, this.keyDownHandler);
            window.addEventListener(WindowEventMap.mousemove, this.mouseMoveHandler);
            window.addEventListener(WindowEventMap.mouseout, this.mouseOutHandler);
        }
    }
    mouseOutHandler() {
        if (this.delegate.shouldListenForKeyDown) {
            this.clearSelections(true);
            if (this.delegate.onKeySelectionChange) {
                this.delegate.onKeySelectionChange(this.delegate.options[this.selectedIndex], true);
            }
        }
    }
    mouseMoveHandler(event) {
        if (this.delegate.shouldListenForKeyDown) {
            const path = eventPath(event);
            const currentSelection = path.find((elm) => this.delegate.options.find((option) => option === elm));
            const currentSelection2 = path.find((elm) => elm.dataset && elm.dataset.bxfId);
            if (currentSelection instanceof HTMLElement) {
                this.selectedIndex = this.delegate.options.findIndex((elm) => elm === currentSelection);
                this.clearSelections(undefined, true);
            }
            else if (currentSelection2) {
                this.selectedIndex = this.delegate.options.findIndex((elm) => elm.bxfId === currentSelection2.dataset.bxfId);
                this.clearSelections(undefined, true);
            }
            else {
                this.clearSelections(true, true);
            }
            if (this.delegate.onKeySelectionChange) {
                this.delegate.onKeySelectionChange(this.delegate.options[this.selectedIndex], true);
            }
        }
    }
    clearSelections(clearIndex, hasHoverStyle, force) {
        for (const option of this.delegate.options) {
            if (!force) {
                if (option.metadata && option.metadata.shouldKeepHover) {
                    continue;
                }
            }
            option.isHover = false;
            option.hasHoverStyle = !!hasHoverStyle;
        }
        if (clearIndex) {
            this.selectedIndex = -1;
        }
    }
    selectCurrentSuggestion() {
        if (this.selectedIndex > this.delegate.options.length) {
            this.selectedIndex = 0;
        }
        this.clearSelections(undefined, undefined, true);
        const currentSelection = this.delegate.options[this.selectedIndex];
        if (currentSelection) {
            currentSelection.isHover = true;
            if (currentSelection instanceof HTMLElement) {
                currentSelection.scrollIntoView({
                    block: 'nearest',
                });
            }
        }
        if (this.delegate.onKeySelectionChange) {
            this.delegate.onKeySelectionChange(currentSelection, false);
        }
    }
    selectDown() {
        this.selectedIndex += 1;
        if (this.selectedIndex >= this.delegate.options.length) {
            this.selectedIndex = 0;
        }
        this.selectCurrentSuggestion();
    }
    selectUp() {
        this.selectedIndex -= 1;
        if (this.selectedIndex < 0) {
            this.selectedIndex = this.delegate.options.length - 1;
        }
        this.selectCurrentSuggestion();
    }
    didPressEscape() {
        this.selectedIndex = -1;
        if (this.delegate.onKeyEscape) {
            this.delegate.onKeyEscape();
        }
    }
    keyDownHandler(event) {
        if (this.delegate.shouldListenForKeyDown) {
            switch (event.key) {
                case KeyboardEventKey.ArrowUp:
                    event.preventDefault();
                    this.selectUp();
                    break;
                case KeyboardEventKey.ArrowDown:
                    event.preventDefault();
                    this.selectDown();
                    break;
                case KeyboardEventKey.Escape:
                    event.preventDefault();
                    this.didPressEscape();
                    break;
                default:
                    this.clearSelections();
                    break;
            }
        }
    }
    destroy() {
        window.removeEventListener(WindowEventMap.keydown, this.keyDownHandler);
        window.removeEventListener(WindowEventMap.mousemove, this.mouseMoveHandler);
    }
}

export { BxfSelectKeyDownDelegate as B, getTextWidth as g, sortPredicate as s };
